import { TAU } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { createRng } from '../../core/rng';
import type { RenderTarget, TechniqueContext } from '../types';
import type { DisplacementValues } from './params';

/**
 * Contour displacement: pushes outline points along their own normals.
 *
 * Normals are computed from the polygon itself rather than from the field gradient.
 * Close to a thin stem the gradient of the distance field flips sign across the
 * medial axis, which would make opposite sides of the stem displace *towards* each
 * other; the local edge tangent has no such ambiguity.
 */
export interface DisplacedContour {
  points: Float32Array;
}

export interface DisplacementState {
  rings: DisplacedContour[][];
  palette: ResolvedPalette;
  built: number;
  targetRings: number;
}

function values(ctx: TechniqueContext): DisplacementValues {
  return ctx.values as unknown as DisplacementValues;
}

export function init(ctx: TechniqueContext): DisplacementState {
  const v = values(ctx);
  return {
    rings: [],
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    built: 0,
    targetRings: v.contours,
  };
}

export function step(state: DisplacementState, _dt: number, ctx: TechniqueContext): DisplacementState {
  const v = values(ctx);
  if (state.built >= state.targetRings) return state;

  const ring = state.built;
  const amplitude = v.amplitude * (1 + ring * v.amplitudeGrowth * 0.25);
  const baseOffset = ring * v.offset;
  // Each ring gets its own deterministic stream, so rings do not share a wobble.
  const rng = createRng(ctx.rng.seed + ring * 7919);
  const phase = rng.next() * TAU;

  const out: DisplacedContour[] = [];

  for (const contour of ctx.field.contours) {
    const p = contour.points;
    const n = p.length / 2;
    if (n < 3) continue;

    // Outward normal per vertex from the averaged adjacent edge directions.
    const nx = new Float32Array(n);
    const ny = new Float32Array(n);
    const winding = contour.signedArea >= 0 ? 1 : -1;
    for (let i = 0; i < n; i++) {
      const prev = (i - 1 + n) % n;
      const next = (i + 1) % n;
      const tx = p[next * 2] - p[prev * 2];
      const ty = p[next * 2 + 1] - p[prev * 2 + 1];
      const len = Math.hypot(tx, ty) || 1;
      nx[i] = (ty / len) * winding;
      ny[i] = (-tx / len) * winding;
    }

    // Displacement amount per vertex, as a function of arc position.
    const amounts = new Float32Array(n);
    let walked = 0;
    for (let i = 0; i < n; i++) {
      const next = (i + 1) % n;
      const seg = Math.hypot(p[next * 2] - p[i * 2], p[next * 2 + 1] - p[i * 2 + 1]);
      const t = contour.length > 0 ? walked / contour.length : 0;
      walked += seg;

      let value: number;
      if (v.jagged) value = rng.next() * 2 - 1;
      else value = Math.sin(t * TAU * v.frequency + phase);

      if (v.mode === 'out') value = Math.abs(value);
      else if (v.mode === 'in') value = -Math.abs(value);
      amounts[i] = value * amplitude;
    }

    // Smoothing: a few box passes over the amounts, wrapping around the ring.
    for (let pass = 0; pass < v.smoothing; pass++) {
      const copy = Float32Array.from(amounts);
      for (let i = 0; i < n; i++) {
        amounts[i] = (copy[(i - 1 + n) % n] + copy[i] * 2 + copy[(i + 1) % n]) / 4;
      }
    }

    const points = new Float32Array(p.length);
    for (let i = 0; i < n; i++) {
      const d = amounts[i] + baseOffset;
      points[i * 2] = p[i * 2] + nx[i] * d;
      points[i * 2 + 1] = p[i * 2 + 1] + ny[i] * d;
    }
    out.push({ points });
  }

  state.rings.push(out);
  state.built++;
  return state;
}

export function isComplete(state: DisplacementState): boolean {
  return state.built >= state.targetRings;
}

function ringPath(ring: DisplacedContour[]): Path2D {
  const path = new Path2D();
  for (const contour of ring) {
    const p = contour.points;
    if (p.length < 6) continue;
    path.moveTo(p[0], p[1]);
    for (let i = 2; i < p.length; i += 2) path.lineTo(p[i], p[i + 1]);
    path.closePath();
  }
  return path;
}

export function draw(state: DisplacementState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  c.lineWidth = v.lineWidth;
  c.lineJoin = 'round';

  // Outermost first so the innermost contour stays on top when filled.
  for (let i = state.rings.length - 1; i >= 0; i--) {
    const t = state.rings.length > 1 ? i / (state.rings.length - 1) : 0;
    const color = state.palette.at(t, v.opacity);
    const path = ringPath(state.rings[i]);
    if (v.style === 'fill') {
      c.fillStyle = color;
      c.fill(path, 'nonzero');
    } else {
      c.strokeStyle = color;
      c.stroke(path);
    }
  }
}

export function elementCount(state: DisplacementState): number {
  let n = 0;
  for (const ring of state.rings) for (const contour of ring) n += contour.points.length / 2;
  return n;
}

export function toSVG(state: DisplacementState, ctx: TechniqueContext): string {
  const v = values(ctx);
  const f = (n: number): string => n.toFixed(2).replace(/\.?0+$/, '');
  const parts: string[] = [`<g stroke-width="${v.lineWidth}" stroke-linejoin="round">`];

  for (let i = state.rings.length - 1; i >= 0; i--) {
    const t = state.rings.length > 1 ? i / (state.rings.length - 1) : 0;
    const color = state.palette.at(t);
    let d = '';
    for (const contour of state.rings[i]) {
      const p = contour.points;
      if (p.length < 6) continue;
      d += `M${f(p[0])} ${f(p[1])}`;
      for (let k = 2; k < p.length; k += 2) d += `L${f(p[k])} ${f(p[k + 1])}`;
      d += 'Z';
    }
    if (!d) continue;
    parts.push(
      v.style === 'fill'
        ? `<path fill="${color}" fill-opacity="${v.opacity}" fill-rule="nonzero" d="${d}"/>`
        : `<path fill="none" stroke="${color}" stroke-opacity="${v.opacity}" d="${d}"/>`,
    );
  }

  parts.push('</g>');
  return parts.join('\n');
}
